import React, { useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface UserStatusModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  loading: boolean
  userName: string
  action: 'activate' | 'deactivate'
}

const UserStatusModal: React.FC<UserStatusModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  userName,
  action
}) => {
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, loading, onClose])

  if (!isOpen) return null

  const isActivate = action === 'activate'
  const title = isActivate ? 'Activar usuario' : 'Desactivar usuario'
  const description = isActivate
    ? 'El usuario podrá volver a ingresar al sistema.'
    : 'El usuario no podrá ingresar al sistema hasta ser activado nuevamente.'
  const buttonLabel = isActivate ? 'Activar' : 'Desactivar'
  const buttonClass = isActivate ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose()
      }}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            disabled={loading}
            aria-label="Cerrar modal de confirmación"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
          ¿Confirmas que deseas {isActivate ? 'activar' : 'desactivar'} a <strong>{userName}</strong>?
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${buttonClass}`}
          >
            {loading ? 'Procesando...' : buttonLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserStatusModal
